import React from "react";
import {useTranslation} from "react-i18next";
import {ChangeDateFormat} from "../../../Common/Helpers/ChangeDateFormat";
import {ToCustomDecimal} from "../../../AppSystem/Helpers/ToCustomDecimal";
import {CollapsibleSection} from "../../../Common/Components/Detail/CollapsibleSection";
import {Field, FieldBlock} from "../../../Common/Components/Detail/FieldBlock";
import {FileLink} from "../../../Common/Components/Detail/FileLink";

const kr = (value) => `${ToCustomDecimal(value ?? 0)} kr`;

/*
Sections of an attest of model "Betala": the payment itself and its attachments.
*/
export function AttestPaySections({data}) {
    const {t} = useTranslation();

    const typeLabel = data?.type === "Bankkonto" ? t('payReceive.bankAccount') : t('payReceive.bankgiro');
    // The section is always there; its header says whether it holds anything.
    const attachmentsCount = (data?.additionalInfo?.participantsFileId ? 1 : 0)
        + (data?.additionalInfo?.program ? 1 : 0);
    const attachmentsSubtitle = () => {
        if (attachmentsCount === 0) return t('attest.noAttachments');
        if (attachmentsCount === 1) return t('attest.attachmentOne');
        return t('attest.attachmentsCount', {count: attachmentsCount});
    };

    return (
        <>
            <CollapsibleSection
                badge="B"
                title={t('payReceive.pay')}
                subtitle={typeLabel}
                amountLabel={`${t('reimbursement.amount')}:`}
                amount={kr(data?.amount)}
                defaultOpen={true}
            >
                <FieldBlock>
                    <Field label={t('modelLabel.creditorName')} value={data?.creditorName} wide />
                    {data?.clearingNumber ?
                        <Field label={t('userSetting.modelClearingNumber')} value={data.clearingNumber} />
                    : ''}
                    {data?.accountNumber ?
                        <Field label={t('userSetting.modelAccountNumber')} value={data.accountNumber} />
                    : ''}
                    {data?.bg ? <Field label="BG" value={data.bg} /> : ''}
                    {data?.pg ? <Field label="PG" value={data.pg} /> : ''}
                    {data?.ocr ? <Field label="OCR" value={data.ocr} /> : ''}
                </FieldBlock>

                <FieldBlock>
                    <Field
                        label={t('reimbursement.costBearer')}
                        value={data?.showLabel ? data.showLabel : data?.costBearerName}
                        wide
                    />
                    <Field label={t('modelLabel.accounting')} value={data?.accountingName} />
                    <Field
                        label={t('modelLabel.whenToPay')}
                        value={data?.date ? ChangeDateFormat('date', data.date) : t('modelLabel.soon')}
                    />
                    {data?.vatAmount ?
                        <Field label={t('modelLabel.vatAmount')} value={kr(data.vatAmount)} />
                    : ''}
                    <Field
                        label={t('reimbursement.receipt')}
                        value={<FileLink fileId={data?.fileId} fileName={data?.fileName} />}
                    />
                    <Field label={t('reimbursement.amount')} value={kr(data?.amount)} accent strong />
                </FieldBlock>

                {data?.description || data?.message ?
                    <FieldBlock>
                        {data?.description ?
                            <Field label={t('modelLabel.description')} value={data.description} wide />
                        : ''}
                        {data?.message ?
                            <Field label={t('modelLabel.message')} value={data.message} wide />
                        : ''}
                    </FieldBlock>
                : ''}
            </CollapsibleSection>

            <CollapsibleSection
                title={t('reimbursement.participantsAndPrograms')}
                subtitle={attachmentsSubtitle()}
                collapsible={attachmentsCount > 0}
            >
                {attachmentsCount > 0 ?
                    <FieldBlock>
                        {data?.additionalInfo?.participantsFileId ?
                            <Field
                                label={t('attest.participantsList')}
                                value={<FileLink fileId={data.additionalInfo.participantsFileId} fileName={data.additionalFileName} />}
                            />
                        : ''}
                        {data?.additionalInfo?.program ?
                            <Field
                                label={t('reimbursement.programInvitation')}
                                value={data.additionalInfo.program}
                                wide
                            />
                        : ''}
                    </FieldBlock>
                : ''}
            </CollapsibleSection>
        </>
    );
}
